import React, { useState, useMemo, useCallback } from "react";
import { withAuth } from "../api/auth.ts";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Stack,
  Divider,
  Select,
  MenuItem,
  FormControl,
  IconButton,
} from "@mui/material";
import {
  FolderSharedTwoTone as SharedIcon,
  ArrowUpward as AscIcon,
  ArrowDownward as DescIcon,
} from "@mui/icons-material";
import { formatFileSize, formatDate } from "../utils/formatUtils.ts";
import { usePaletteColors } from "../hooks/useDarkMode.ts";
import { PaletteColor } from "../types/PaletteColor.ts";
import { FileThumbnail } from "./FileThumbnail.tsx";
import { FileTypeIcon } from "./FileTypeIcon.tsx";

// API の DTO
export interface MyFileItemDto {
  fileId: string;
  fileName: string;
  fileSize: number;
  contentType: string | null;
  fileExtension: string;
  updatedAt: string;
  hpcode: number;
  roomId: number;
  roomName: string;
  isBookmarked: boolean;
  thumbnailStatus: number; // 0:未生成, 1:生成済, 2:非対応・生成失敗
}

interface SharedFilesContentProps {
  files: MyFileItemDto[];
  isLoading: boolean;
  honbuId: number;
  palette: PaletteColor;
  onFileClick: (file: MyFileItemDto) => void;
}

export const SharedFilesContent: React.FC<SharedFilesContentProps> = ({
  files,
  isLoading,
  honbuId,
  palette,
  onFileClick,
}) => {
  const { isDarkMode, accentColor } = usePaletteColors(palette);

  // サムネイル取得 URL (thumbnailStatus=2 [非対応・生成失敗] のファイルはリクエストしない)
  const getThumbnailUrl = (file: MyFileItemDto): string | undefined =>
    file.thumbnailStatus !== 2
      ? withAuth(`/api/files/thumbnail/${file.fileId}?honbuId=${honbuId}&roomId=${file.roomId}`)
      : undefined;

  type SortKey = "fileName" | "roomName" | "updatedAt" | "fileSize";
  type SortDirection = "asc" | "desc";
  const [sortKey, setSortKey] = useState<SortKey>("updatedAt");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  const sortKeyLabels: Record<SortKey, string> = {
    fileName: "ファイル名",
    roomName: "部屋",
    updatedAt: "更新日",
    fileSize: "サイズ",
  };

  const handleSort = useCallback(
    (key: SortKey) => {
      setSortDirection((prev) =>
        sortKey === key ? (prev === "asc" ? "desc" : "asc") : "desc",
      );
      setSortKey(key);
    },
    [sortKey],
  );

  const sortedFiles = useMemo(() => {
    return [...files].sort((a, b) => {
      const dir = sortDirection === "asc" ? 1 : -1;
      switch (sortKey) {
        case "fileName":
          return dir * a.fileName.localeCompare(b.fileName, "ja");
        case "roomName":
          return dir * (a.roomName ?? "").localeCompare(b.roomName ?? "", "ja");
        case "updatedAt":
          return (
            dir *
            (new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime())
          );
        case "fileSize":
          return dir * (a.fileSize - b.fileSize);
        default:
          return 0;
      }
    });
  }, [files, sortKey, sortDirection]);

  if (isLoading) return <Box sx={{ p: 3 }}>Now Loading...</Box>;

  const textShadowStyle = {
    color: `${accentColor}`,
    textShadow: `
      -1px -1px 0 "#FFFFFF",
       1px -1px 0 "#FFFFFF",
      -1px  1px 0 "#FFFFFF",
       1px  1px 0 "#FFFFFF",
       2px  2px 3px rgba(0,0,0,0.3)
    `,
  };

  return (
    <Box sx={{ p: 4, height: "100%", overflowY: "auto" }}>
      {/* タイトルセクション */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 800,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 1,
            letterSpacing: "0.03em",
            ...textShadowStyle,
          }}
        >
          <SharedIcon
            sx={{
              color: `${accentColor}`,
              fontSize: "1.3em",
            }}
          />
          共有中のファイル
        </Typography>

        <Divider sx={{ borderColor: isDarkMode ? "grey.800" : "grey.200" }} />
      </Box>

      {files.length === 0 ? (
        <Typography color="text.secondary">
          共有中のファイルはありません。
        </Typography>
      ) : (
        <>
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            sx={{ mb: 1.5 }}
          >
            <FormControl size="small" sx={{ minWidth: 0 }}>
              <Select
                value={sortKey}
                onChange={(e) => handleSort(e.target.value as SortKey)}
                variant="standard"
                disableUnderline
                sx={{
                  fontSize: "0.8rem",
                  color: "text.secondary",
                  "& .MuiSelect-select": { py: 0, pr: "20px !important" },
                }}
              >
                {(Object.keys(sortKeyLabels) as SortKey[]).map((key) => (
                  <MenuItem key={key} value={key} dense>
                    {sortKeyLabels[key]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <IconButton
              size="small"
              onClick={() =>
                setSortDirection((d) => (d === "asc" ? "desc" : "asc"))
              }
              sx={{ color: "text.secondary" }}
            >
              {sortDirection === "asc" ? (
                <AscIcon sx={{ fontSize: 18 }} />
              ) : (
                <DescIcon sx={{ fontSize: 18 }} />
              )}
            </IconButton>
          </Stack>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead sx={{ bgcolor: "action.hover" }}>
                <TableRow>
                  <TableCell
                    sortDirection={
                      sortKey === "fileName" ? sortDirection : false
                    }
                  >
                    <TableSortLabel
                      active={sortKey === "fileName"}
                      direction={sortKey === "fileName" ? sortDirection : "asc"}
                      onClick={() => handleSort("fileName")}
                    >
                      ファイル名
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sortDirection={
                      sortKey === "roomName" ? sortDirection : false
                    }
                  >
                    <TableSortLabel
                      active={sortKey === "roomName"}
                      direction={sortKey === "roomName" ? sortDirection : "asc"}
                      onClick={() => handleSort("roomName")}
                    >
                      部屋
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sortDirection={
                      sortKey === "updatedAt" ? sortDirection : false
                    }
                  >
                    <TableSortLabel
                      active={sortKey === "updatedAt"}
                      direction={
                        sortKey === "updatedAt" ? sortDirection : "asc"
                      }
                      onClick={() => handleSort("updatedAt")}
                    >
                      更新日
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    align="right"
                    sortDirection={
                      sortKey === "fileSize" ? sortDirection : false
                    }
                  >
                    <TableSortLabel
                      active={sortKey === "fileSize"}
                      direction={sortKey === "fileSize" ? sortDirection : "asc"}
                      onClick={() => handleSort("fileSize")}
                    >
                      サイズ
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedFiles.map((file) => (
                  <TableRow
                    key={file.fileId}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => onFileClick(file)}
                  >
                    <TableCell>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        {getThumbnailUrl(file) ? (
                          <FileThumbnail
                            src={getThumbnailUrl(file)!}
                            size={28}
                            fallback={
                              <Box
                                sx={{ color: "text.secondary", display: "flex" }}
                              >
                                <FileTypeIcon
                                  fileName={file.fileName}
                                  size={24}
                                />
                              </Box>
                            }
                          />
                        ) : (
                          <Box sx={{ color: "text.secondary", display: "flex" }}>
                            <FileTypeIcon fileName={file.fileName} size={24} />
                          </Box>
                        )}
                        <Typography variant="body2" fontWeight={500}>
                          {file.fileName}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {file.roomName || `Room: ${file.roomId}`}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {formatDate(file.updatedAt)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="caption">
                        {formatFileSize(file.fileSize)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
};
